/**
 * GOMEZ / FM product page scripts.
 *
 * Loaded only on the product template (see sections/product.liquid),
 * deferred, after assets/theme.js. Every piece below is a self-contained
 * IIFE guarded against its markup being absent — most blocks here are
 * optional (a single-variant product has no variant picker, a merchant
 * may disable the sticky bar, etc.) — so nothing assumes any of this
 * HTML exists.
 *
 * Everything is progressive enhancement on top of a real, working
 * <form>: a <noscript> variant <select> and standard form POST already
 * let the product be purchased with JavaScript disabled.
 */

/**
 * Shared money formatter, the standard technique for turning a variant's
 * price (an integer in cents, from the embedded variant JSON) into a
 * display string using the shop's own configured money_format — no
 * currency/locale guessing.
 */
function gomezFormatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  function withDelimiters(number, precision, thousands, decimal) {
    number = (number / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var fraction = parts[1] ? decimal + parts[1] : '';
    return dollars + fraction;
  }

  var formatString = format || '${{amount}}';
  var placeholderMatch = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
  if (!placeholderMatch) {
    return formatString;
  }

  var value;
  switch (placeholderMatch[1]) {
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = withDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = withDelimiters(cents, 0, ' ', '');
      break;
    default:
      value = withDelimiters(cents, 2, ',', '.');
  }

  return formatString.replace(placeholderMatch[0], value);
}

/**
 * Product strings shared by the controllers below — translated text JS
 * can't produce itself. Rendered once by sections/product.liquid as
 * `<script type="application/json" data-product-strings>`, parsed with
 * the shared gomezReadJSON() helper from assets/theme.js (loaded first).
 */
var gomezProductStrings =
  gomezReadJSON(document.querySelector('[data-product-strings]')) || {};

/**
 * Variant controller.
 *
 * HTML contract:
 *   - `[data-variant-picker]` wraps everything, containing:
 *     - `[data-variant-json]` — `product.variants` as JSON
 *     - `[data-variant-option="N"]` — a radio (name shared per option) or
 *       a <select>, one group per option position
 *   - `#product-form` (set via `{% form 'product', product, id: 'product-form' %}`
 *     in sections/product.liquid) has `input[name="id"]` (hidden, always
 *     present — rendered unconditionally by blocks/product-buy-buttons.liquid)
 *   - Optional, updated when present: `[data-variant-price-target]`,
 *     `[data-variant-sku]`, `[data-variant-unavailable-note]`,
 *     `[data-add-to-cart-button]`
 *   - Optional, for variant-linked media: gallery slides/thumbnails
 *     carrying `data-media-id`
 *
 * Initial state always matches `product.selected_or_first_available_variant`
 * (computed server-side in Liquid) — this controller only reacts to
 * user-driven changes, it never guesses an initial variant itself.
 */
(function () {
  'use strict';

  var picker = document.querySelector('[data-variant-picker]');
  var form = document.getElementById('product-form');
  if (!form) {
    return;
  }

  var idInput = form.querySelector('input[name="id"]');
  var moneyFormat = gomezProductStrings.moneyFormat || '${{amount}}';

  var variants = picker ? gomezReadJSON(picker.querySelector('[data-variant-json]')) : null;
  if (!picker || !variants) {
    return;
  }

  var optionInputs = picker.querySelectorAll('[data-variant-option]');

  function getSelectedOptions() {
    var selections = {};
    optionInputs.forEach(function (input) {
      var position = input.getAttribute('data-variant-option');
      if (input.tagName === 'SELECT') {
        selections[position] = input.value;
      } else if (input.checked) {
        selections[position] = input.value;
      }
    });
    return selections;
  }

  function findMatchingVariant(selections) {
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var matches = true;
      if (selections['1'] !== undefined && variant.option1 !== selections['1']) {
        matches = false;
      }
      if (matches && selections['2'] !== undefined && variant.option2 !== selections['2']) {
        matches = false;
      }
      if (matches && selections['3'] !== undefined && variant.option3 !== selections['3']) {
        matches = false;
      }
      if (matches) {
        return variant;
      }
    }
    return null;
  }

  function renderPriceInto(target, variant) {
    if (!target) {
      return;
    }
    var isSoldOut = !variant.available;
    var isSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    var classes = 'price';
    if (isSale) {
      classes += ' price--sale';
    }
    if (isSoldOut) {
      classes += ' price--sold-out';
    }

    var html;
    if (isSoldOut) {
      html =
        '<span class="price__sold-out">' + (gomezProductStrings.soldOut || '') + '</span>';
    } else if (isSale) {
      html =
        '<span class="visually-hidden">' + (gomezProductStrings.regularPrice || '') + '</span>' +
        '<s class="price__compare-at">' + gomezFormatMoney(variant.compare_at_price, moneyFormat) + '</s>' +
        '<span class="visually-hidden">' + (gomezProductStrings.salePrice || '') + '</span>' +
        '<span class="price__sale">' + gomezFormatMoney(variant.price, moneyFormat) + '</span>';
    } else {
      html =
        '<span class="visually-hidden">' + (gomezProductStrings.regularPrice || '') + '</span>' +
        '<span class="price__regular">' + gomezFormatMoney(variant.price, moneyFormat) + '</span>';
    }

    target.setAttribute('class', classes);
    target.innerHTML = html;
  }

  function updateMedia(variant) {
    if (!variant.featured_image) {
      return;
    }
    var mediaId = variant.featured_image.id;
    if (!mediaId) {
      return;
    }
    var track = document.querySelector('[data-product-media-track]');
    var slide = track && track.querySelector('[data-media-id="' + mediaId + '"]');
    if (slide) {
      slide.scrollIntoView({ inline: 'start', block: 'nearest' });
    }
    var thumbnails = document.querySelectorAll('[data-product-thumbnail]');
    thumbnails.forEach(function (thumbnail) {
      if (thumbnail.getAttribute('data-media-id') === String(mediaId)) {
        thumbnail.setAttribute('aria-current', 'true');
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    });
  }

  function updateAddToCartButtons(variant) {
    // snippets/button.liquid always wraps its visible text in a
    // `.button__label` span — target that rather than assuming our own
    // markup structure, since these buttons are rendered by that snippet.
    var buttons = document.querySelectorAll('[data-add-to-cart-button]');
    buttons.forEach(function (button) {
      button.disabled = !variant.available;
      var label = button.querySelector('.button__label') || button;
      label.textContent = variant.available
        ? gomezProductStrings.addToCart || ''
        : gomezProductStrings.soldOut || '';
    });
  }

  function updateForVariant(variant) {
    if (!variant) {
      var note = picker.querySelector('[data-variant-unavailable-note]');
      if (note) {
        note.hidden = false;
      }
      return;
    }

    var note = picker.querySelector('[data-variant-unavailable-note]');
    if (note) {
      note.hidden = true;
    }

    if (idInput) {
      idInput.value = variant.id;
    }

    document.querySelectorAll('[data-variant-price-target]').forEach(function (target) {
      renderPriceInto(target, variant);
    });

    var skuTarget = picker.querySelector('[data-variant-sku]');
    if (skuTarget) {
      skuTarget.textContent = variant.sku || '';
    }

    updateAddToCartButtons(variant);
    updateMedia(variant);

    if (window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    form.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant } }));
  }

  optionInputs.forEach(function (input) {
    input.addEventListener('change', function () {
      var variant = findMatchingVariant(getSelectedOptions());
      updateForVariant(variant);
    });
  });
})();

/**
 * Gallery controller.
 *
 * HTML contract: `[data-product-media-track]` contains slides with
 * `data-media-id`; `[data-product-thumbnail]` buttons carry a matching
 * `data-media-id`. Clicking a thumbnail scrolls the track to its slide;
 * an IntersectionObserver keeps `aria-current` on the thumbnail matching
 * whatever slide is currently in view (from swiping/scrolling directly).
 */
(function () {
  'use strict';

  var track = document.querySelector('[data-product-media-track]');
  if (!track) {
    return;
  }

  document.querySelectorAll('[data-product-thumbnail]').forEach(function (thumbnail) {
    thumbnail.addEventListener('click', function () {
      var mediaId = thumbnail.getAttribute('data-media-id');
      var slide = track.querySelector('[data-media-id="' + mediaId + '"]');
      if (slide) {
        slide.scrollIntoView({ inline: 'start', block: 'nearest' });
      }
    });
  });

  if (typeof IntersectionObserver === 'undefined') {
    return;
  }

  var slides = track.querySelectorAll('[data-media-id]');
  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          return;
        }
        var mediaId = entry.target.getAttribute('data-media-id');
        document.querySelectorAll('[data-product-thumbnail]').forEach(function (thumbnail) {
          if (thumbnail.getAttribute('data-media-id') === mediaId) {
            thumbnail.setAttribute('aria-current', 'true');
          } else {
            thumbnail.removeAttribute('aria-current');
          }
        });
      });
    },
    { root: track, threshold: 0.6 }
  );
  slides.forEach(function (slide) {
    observer.observe(slide);
  });
})();

/**
 * Sticky buy bar visibility.
 *
 * HTML contract: `[data-sticky-buy-bar-sentinel]` sits at the end of the
 * main buy-buttons block; `[data-sticky-buy-bar]` is shown once the
 * sentinel scrolls out of view above the viewport (i.e. the main buy
 * button has been scrolled past), hidden again when it's back in view.
 */
(function () {
  'use strict';

  var sentinel = document.querySelector('[data-sticky-buy-bar-sentinel]');
  var bar = document.querySelector('[data-sticky-buy-bar]');
  if (!sentinel || !bar || typeof IntersectionObserver === 'undefined') {
    return;
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      bar.hidden = entry.isIntersecting;
    });
  });
  observer.observe(sentinel);

  var form = document.getElementById('product-form');
  if (form) {
    form.addEventListener('variant:change', function (event) {
      var variant = event.detail.variant;
      var priceTarget = bar.querySelector('[data-sticky-buy-bar-price] .price');
      if (priceTarget) {
        var isSoldOut = !variant.available;
        var isSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        var moneyFormat = gomezProductStrings.moneyFormat || '${{amount}}';
        var html;
        if (isSoldOut) {
          html = '<span class="price__sold-out">' + (gomezProductStrings.soldOut || '') + '</span>';
        } else if (isSale) {
          html =
            '<s class="price__compare-at">' + gomezFormatMoney(variant.compare_at_price, moneyFormat) + '</s>' +
            '<span class="price__sale">' + gomezFormatMoney(variant.price, moneyFormat) + '</span>';
        } else {
          html = '<span class="price__regular">' + gomezFormatMoney(variant.price, moneyFormat) + '</span>';
        }
        priceTarget.className = 'price' + (isSale ? ' price--sale' : '') + (isSoldOut ? ' price--sold-out' : '');
        priceTarget.innerHTML = html;
      }

      var submit = bar.querySelector('[data-sticky-buy-bar-submit]');
      if (submit) {
        submit.disabled = !variant.available;
      }
    });
  }
})();

/**
 * AJAX add-to-cart, with a full standard-form fallback.
 *
 * On success: adds via the cart's JSON endpoint, then calls the shared
 * gomezRefreshCartChrome() (assets/theme.js) so the cart drawer and cart
 * count reflect the real, server-rendered cart state — no cart-rendering
 * logic is duplicated in JavaScript. Opens the cart drawer and announces
 * success. On any failure (network error, non-OK response, fetch
 * unavailable), the form's standard POST submission proceeds normally,
 * which redirects to the cart page via the `return_to` field already on
 * the form.
 *
 * HTML contract:
 *   - `#product-form` is the product <form>
 *   - `[data-ajax-cart-status]` receives error/loading text (role="alert")
 *   - `[data-add-to-cart-button]` is disabled while a request is in flight
 *   - `#cart-drawer` and `[data-cart-count]` are refreshed by
 *     gomezRefreshCartChrome() when present
 */
(function () {
  'use strict';

  var form = document.getElementById('product-form');
  if (!form || typeof window.fetch !== 'function') {
    return;
  }

  var statusRegion = document.querySelector('[data-ajax-cart-status]');
  var isSubmitting = false;

  function setLoading(loading) {
    document.querySelectorAll('[data-add-to-cart-button]').forEach(function (button) {
      button.disabled = loading;
      button.classList.toggle('is-loading', loading);
    });
  }

  function announce(message, isError) {
    if (!statusRegion) {
      return;
    }
    statusRegion.setAttribute('role', isError ? 'alert' : 'status');
    statusRegion.textContent = message;
  }

  form.addEventListener('submit', function (event) {
    if (isSubmitting) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    isSubmitting = true;
    setLoading(true);
    announce(gomezProductStrings.adding || '', false);

    var formData = new FormData(form);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (data) {
            throw new Error((data && data.description) || 'Add to cart failed');
          });
        }
        return response.json();
      })
      .then(function () {
        return gomezRefreshCartChrome();
      })
      .then(function () {
        announce(gomezProductStrings.addedToCart || '', false);

        var cartDrawer = document.getElementById('cart-drawer');
        var drawerStatus = cartDrawer && cartDrawer.querySelector('[data-cart-drawer-status]');
        if (drawerStatus) {
          drawerStatus.textContent = gomezProductStrings.addedToCart || '';
        }

        var cartDrawerTrigger = document.querySelector('[data-dialog-open="cart-drawer"]');
        if (cartDrawer && typeof cartDrawer.showModal === 'function' && !cartDrawer.open) {
          cartDrawer.showModal();
        } else if (cartDrawerTrigger) {
          cartDrawerTrigger.click();
        }
      })
      .catch(function () {
        announce(gomezProductStrings.addError || '', true);
        // Fall back to a real, standard form submission so the purchase
        // still completes even if the AJAX path failed.
        isSubmitting = false;
        setLoading(false);
        HTMLFormElement.prototype.submit.call(form);
        return;
      })
      .then(function () {
        isSubmitting = false;
        setLoading(false);
      });
  });
})();

/**
 * Product share button.
 *
 * HTML contract: `[data-product-share]` carries `data-share-url` and
 * `data-share-title`; its `[data-product-share-label]` child gets a
 * brief "Copied" confirmation after a successful clipboard copy.
 *
 * Uses the native Web Share API where available (mobile), falling back
 * to clipboard copy. Restrained by design — one button, no share-icon row.
 */
(function () {
  'use strict';

  document.querySelectorAll('[data-product-share]').forEach(function (button) {
    button.addEventListener('click', function () {
      var url = button.getAttribute('data-share-url');
      var title = button.getAttribute('data-share-title');

      if (navigator.share) {
        navigator.share({ url: url, title: title }).catch(function () {
          // User cancelled or share failed silently; nothing else to do.
        });
        return;
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () {
          var label = button.querySelector('[data-product-share-label]');
          if (label && gomezProductStrings.linkCopied) {
            var original = label.textContent;
            label.textContent = gomezProductStrings.linkCopied;
            setTimeout(function () {
              label.textContent = original;
            }, 2000);
          }
        });
      }
    });
  });
})();
